import {
    CheckCircle2Icon,
    ChevronLeftIcon,
    ChevronRightIcon,
    CircleGaugeIcon,
    PlayIcon,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { twMerge } from 'tailwind-merge';
import modalStore from '../../../stores/modalStore';
import ChapterCompleteModalContent from '../modal/ChapterCompleteModalContent';
import { useChapterStore } from '../provider/ChapterProvider';
import { useLessonStore } from '../provider/LessonProvider';
import PlaybackBar from './PlaybackBar';
import _ from 'lodash';

const BUFFER_TIME = 0.3;

const PlaybackControl = observer(() => {
    const lessonStore = useLessonStore();
    const chapterStore = useChapterStore();
    const lessonPlayerRef = lessonStore.lessonPlayerRef;

    const playCurrentSentence = () => {
        const sentence = chapterStore.currentSentence;
        if (sentence) {
            const startTime = sentence.startTime - BUFFER_TIME;

            // PlaybackBar 에서 문장 시간이 지나면 멈추는 기능이 있음
            // mobx 스토어의 값이 한 번 업데이트 되는 걸 기다리고 재생함
            // 사용하기에도 살짝 딜레이가 있는 것이 느낌이 좋음
            _.delay(() => {
                lessonPlayerRef?.current?.seekToAndPlay(startTime);

                // 문장 재생 시작
                // isSentencePlaying 플래그를 주는 것은 '멈추는 것'을 결정하기 위한 목적임
                // 재생을 시작하자마자 범위를 벗어나면서 멈춰버리는 문제가 있기 때문에,
                // 플래그 설정 타이밍을 살짝 늦춰서 문제가 없도록 함
                _.delay(() => {
                    chapterStore.startSentencePlaying();
                }, 200);
            }, 100);
        }
    };

    const handlePrevious = () => {
        // 자동 재생 중이면 종료
        if (chapterStore.isAutoPlaying) {
            chapterStore.stopAutoPlay();
            lessonPlayerRef?.current?.pause();
        }

        chapterStore.moveToPrevSentence();
        playCurrentSentence();
    };

    const handlePlayOrStop = () => {
        if (chapterStore.isAutoPlaying) {
            // 자동 재생 중지
            chapterStore.stopAutoPlay();
            lessonPlayerRef?.current?.pause();
        } else {
            // 현재 문장 재생
            playCurrentSentence();
        }
    };

    const handleNext = () => {
        // 자동 재생 중이면 종료
        if (chapterStore.isAutoPlaying) {
            chapterStore.stopAutoPlay();
            lessonPlayerRef?.current?.pause();
        }

        chapterStore.moveToNextSentence();
        playCurrentSentence();
    };

    const handleChapterComplete = async () => {
        await modalStore.custom({
            renderComponent: ({ modalId }) => (
                <ChapterCompleteModalContent
                    modalId={modalId}
                    lessonStore={lessonStore}
                    chapterStore={chapterStore}
                />
            ),
            showCloseButton: true,
        });
    };

    return (
        <div
            className={twMerge(
                // 컨트롤러 내부에서 반응형 처리하기 위해 컨테이너 클래스 추가
                '@container',
                'flex w-full items-center justify-between gap-4',
            )}
        >
            {/* 이전 문장 */}
            <div className="flex flex-1/5 items-center justify-start">
                <button
                    className={twMerge(
                        'btn btn-sm btn-block flex h-10 max-w-28 items-center justify-center',
                        chapterStore.isFirstSentence && 'btn-disabled',
                    )}
                    onClick={handlePrevious}
                    disabled={chapterStore.isFirstSentence}
                >
                    <ChevronLeftIcon className="size-4 shrink-0" />
                    <div className="hidden flex-1 text-xs @sm:block">
                        이전 <span className="hidden @lg:inline">문장</span>
                    </div>
                </button>
            </div>

            {/* 중앙 컨트롤 */}
            <div className="flex flex-3/5 items-center justify-center">
                <div className="relative w-full max-w-60 pt-2">
                    <div className="absolute -top-2.5 right-0 left-0">
                        <PlaybackBar />
                    </div>
                    {chapterStore.isAutoPlaying ? (
                        <button
                            className="btn btn-sm btn-primary btn-soft btn-block h-10"
                            onClick={handlePlayOrStop}
                        >
                            <CircleGaugeIcon
                                size={20}
                                className="animate-spin [animation-duration:3.5s]"
                            />
                            <div className="hidden flex-1 text-center @[200px]:block">
                                <span className="hidden @[300px]:inline">자동재생</span> 종료
                            </div>
                        </button>
                    ) : (
                        <button
                            className="btn btn-sm btn-action-primary btn-block h-10"
                            onClick={handlePlayOrStop}
                        >
                            <PlayIcon className="size-4 shrink-0" />
                            <div className="hidden flex-1 text-center text-sm @[200px]:block">
                                <span className="hidden @[240px]:inline">문장</span> 재생
                            </div>
                        </button>
                    )}
                </div>
            </div>

            <div className="flex flex-1/5 items-center justify-center">
                {/* 레슨의 최종 마지막 문장인 경우 */}
                {chapterStore.isLastSentence && lessonStore.isLastChapter && (
                    <button
                        className="btn btn-sm btn-primary btn-block flex h-10 max-w-28 items-center justify-center"
                        onClick={() => {
                            // TODO 레슨 완료 처리
                        }}
                    >
                        <div className="flex-1 text-xs">레슨완료</div>
                        <ChevronRightIcon className="size-4 shrink-0" />
                    </button>
                )}
                {/* 챕터의 마지막 문장인 경우 */}
                {chapterStore.isLastSentence && !lessonStore.isLastChapter && (
                    <button
                        className="btn btn-sm btn-primary btn-block flex h-10 max-w-28 items-center justify-center"
                        onClick={handleChapterComplete}
                    >
                        <CheckCircle2Icon className="size-4 shrink-0" />
                        <div className="hidden flex-1 text-center text-xs @sm:block">
                            <span className="hidden @lg:inline">챕터</span> 완료
                        </div>
                    </button>
                )}
                {/* 다음 문장인 경우 */}
                {!chapterStore.isLastSentence && (
                    <button
                        className="btn btn-sm btn-block flex h-10 max-w-28 items-center justify-center"
                        onClick={handleNext}
                    >
                        <div className="hidden flex-1 text-center text-xs @sm:block">
                            다음 <span className="hidden @lg:inline">문장</span>
                        </div>
                        <ChevronRightIcon className="size-4 shrink-0" />
                    </button>
                )}
            </div>
        </div>
    );
});

export default PlaybackControl;
